import { useEffect, useRef } from 'react'

const EDGE_ZONE_PX = 50
const MAX_SCROLL_SPEED = 15

/**
 * ドラッグ中にマウスがグリッドの上端/下端/左端/右端に近づくと自動スクロールするフック。
 * mousemove は既に各オーバーレイで処理されるため、ここでは passive listener で
 * 座標だけを軽量に追跡し、rAF ループでスクロールを実行する。
 */
export const useDragAutoScroll = (
  containerRef: React.RefObject<HTMLElement>,
  isDragging: boolean
) => {
  const mouseXRef = useRef(0)
  const mouseYRef = useRef(0)

  useEffect(() => {
    if (!isDragging) return

    const container = containerRef.current
    if (!container) return

    const viewport = container.querySelector('.ag-body-viewport')
    if (!viewport) return

    // 横スクロールは ag-grid の水平スクロールバー用 viewport で制御
    const hScrollViewport = container.querySelector(
      '.ag-body-horizontal-scroll-viewport'
    )
    // エッジ判定はガントチャートが描画される中央列の viewport を基準にする
    const centerColsViewport = container.querySelector(
      '.ag-center-cols-viewport'
    )

    const handleMouseMove = (e: MouseEvent) => {
      mouseXRef.current = e.clientX
      mouseYRef.current = e.clientY
    }

    document.addEventListener('mousemove', handleMouseMove, { passive: true })

    let rafId: number

    const scroll = () => {
      const rect = viewport.getBoundingClientRect()
      const x = mouseXRef.current
      const y = mouseYRef.current

      // 縦スクロール
      const distFromTop = y - rect.top
      const distFromBottom = rect.bottom - y

      if (distFromTop < EDGE_ZONE_PX && distFromTop >= 0) {
        const ratio = 1 - distFromTop / EDGE_ZONE_PX
        viewport.scrollTop -= Math.round(MAX_SCROLL_SPEED * ratio)
      } else if (distFromBottom < EDGE_ZONE_PX && distFromBottom >= 0) {
        const ratio = 1 - distFromBottom / EDGE_ZONE_PX
        viewport.scrollTop += Math.round(MAX_SCROLL_SPEED * ratio)
      }

      // 横スクロール（中央列 viewport の矩形を基準にエッジ判定）
      const scrollTarget = hScrollViewport ?? viewport
      const hRect = centerColsViewport
        ? centerColsViewport.getBoundingClientRect()
        : rect
      const distFromLeft = x - hRect.left
      const distFromRight = hRect.right - x

      if (distFromLeft < EDGE_ZONE_PX && distFromLeft >= 0) {
        const ratio = 1 - distFromLeft / EDGE_ZONE_PX
        scrollTarget.scrollLeft -= Math.round(MAX_SCROLL_SPEED * ratio)
      } else if (distFromRight < EDGE_ZONE_PX && distFromRight >= 0) {
        const ratio = 1 - distFromRight / EDGE_ZONE_PX
        scrollTarget.scrollLeft += Math.round(MAX_SCROLL_SPEED * ratio)
      }

      rafId = requestAnimationFrame(scroll)
    }

    rafId = requestAnimationFrame(scroll)

    return () => {
      cancelAnimationFrame(rafId)
      document.removeEventListener('mousemove', handleMouseMove)
    }
  }, [containerRef, isDragging])
}
